import React, { forwardRef, useRef, useState } from "react";
import { Box, Grid, List, ListItem, Stack } from "@mui/material";
import html2pdf from "html2pdf.js";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { SwiperSlide } from "swiper/react";
import { PhysicalOnePagerResultStyle } from "./PhysicalOnePagerResultStyle";
import VSNTypography from "../../../shared/VSNTypography";
import VSNLoader from "../../../shared/VSNLoader";
import VSNBreadcrumbs from "../../../shared/VSNBreadcrumbs";
import SwiperCardComponent from "../../assessment/SwiperCardComponent";
import { VSNSwiper } from "../../../shared/VSNSwiper";
import VSNPaper from "../../../shared/VSNPaper";
import { PurpalFlowerBullet, StarIcon } from "../../../shared/icon";
import CustomSlider from "./CustomSlider";
import VSNImage from "../../../shared/VSNImage";
import VSNButton from "../../../shared/VSNButton";
import {
  UltrasoundOrgan,
  ultraSoundPoliceswiperData,
} from "./CPETDashboard/AssessmentConstant";
import MRFResultContainer from "../../../container/MRF.container";
import {
  PDFCommonFAQSection,
  PDFHeader,
  PDFRecommendationSection,
} from "./PhysicalDashboardPDFCommonComponent";

const PrintComponent = forwardRef((props, ref) => {
  const { result, header } = props;
  const classes = PhysicalOnePagerResultStyle();

  return (
    <Box sx={{ visibility: "hidden", height: 0, overflow: "hidden" }}>
      <Box ref={ref}>
        <Stack gap={1}>
          <Stack
            sx={{
              pageBreakAfter: "always",
            }}
            gap={2}
          >
            <PDFHeader title=" Your Ultrasound, Explained" header={header} />

            <Stack
              className={classes.assessmentReportCard}
              sx={{
                background: "#FAF6F0 !important",
              }}
            >
              <VSNTypography
                variant="h3"
                component="h3"
                className={classes.mdHeadingprimary}
                mb={1}
                sx={{
                  fontSize: "16px !important",
                  color: "#B29263 !important",
                  "&:before": { background: "#B29263 !important" },
                }}
              >
                {result?.clinical_summary?.title}
              </VSNTypography>

              <Stack gap={1}>
                {result?.clinical_summary?.content?.map((el, index) => (
                  <Stack direction="row" key={index}>
                    <Box sx={{ height: 12, width: 12 }} mr={2} mt={0.5}>
                      <StarIcon />
                    </Box>
                    <VSNTypography
                      sx={{
                        fontSize: "14px !important",
                        fontWeight: 500,
                        color: "#0A0A0B",
                        lineHeight: "24px",
                      }}
                    >
                      {el?.text}
                    </VSNTypography>
                  </Stack>
                ))}
              </Stack>
            </Stack>

            <Stack
              className={classes.assessmentReportCard}
              sx={{
                background: "#FAF6F0 !important",
              }}
            >
              <VSNTypography
                variant="h3"
                component="h3"
                className={classes.mdHeadingprimary}
                mb={1}
                sx={{
                  fontSize: "16px !important",
                  color: "#B29263 !important",
                  "&:before": { background: "#B29263 !important" },
                }}
              >
                {result?.vasana_insights?.title ?? ""}
              </VSNTypography>
              <VSNTypography component="p" className={classes.desc}>
                {result?.vasana_insights?.description ?? ""}
              </VSNTypography>

              <Stack gap={1} pt={1}>
                {result?.vasana_insights?.content?.map((el, index) => (
                  <Stack direction="row" key={index}>
                    <Box mr={2} mt={0.6}>
                      <PurpalFlowerBullet height="14" width="14" />
                    </Box>
                    <VSNTypography
                      sx={{
                        fontSize: "14px !important",
                        fontWeight: 500,
                        color: "#0A0A0B",
                        lineHeight: "24px",
                      }}
                    >
                      <span style={{ color: "#86650A", fontWeight: 500 }}>
                        {el?.title ?? ""}{" "}
                      </span>
                      <br />
                      {el?.description}
                    </VSNTypography>
                  </Stack>
                ))}
              </Stack>
            </Stack>

            {/* <PDFCommonFAQSection
              title={result?.vasana_insights?.title}
              description={result?.vasana_insights?.description}
              data={result?.vasana_insights?.content}
            /> */}
          </Stack>

          <Stack
            className={classes.assessmentReportCard}
            sx={{
              background: "#FAF6F0 !important",
              pageBreakAfter: "always",
            }}
          >
            <VSNTypography
              variant="h3"
              component="h3"
              className={classes.mdHeadingprimary}
              mb={1}
              sx={{
                fontSize: "16px !important",
                color: "#B29263 !important",
                "&:before": { background: "#B29263 !important" },
              }}
            >
              {result?.doctors_take?.title ?? ""}
            </VSNTypography>
            <VSNTypography
              component="p"
              className={classes.desc}
              sx={{ fontSize: "14px !important" }}
            >
              {result?.doctors_take?.description ?? ""}
            </VSNTypography>

            <Stack gap={1}>
              {result?.doctors_take?.organs?.map(
                ({ icon, title, description, db_key }, index) => {
                  const matchOrgan = UltrasoundOrgan?.find(
                    (item) => item?.key === db_key
                  );
                  return (
                    <Stack key={index} direction="row" alignItems="start">
                      <Box
                        key={index}
                        className="card"
                        sx={{
                          width: "6%",
                          objectFit: "cover",
                          mr: 2,
                          mt: "14px",
                        }}
                      >
                        <VSNImage
                          src={matchOrgan?.svgIcon ?? icon}
                          alt="icon"
                          sx={{ width: "80px", height: "80px" }}
                        />
                      </Box>
                      <Box width="94%">
                        <VSNTypography
                          variant="subtitle1"
                          component="h6"
                          color="#202024"
                          fontWeight={600}
                          lineHeight="22px"
                          mt={1}
                        >
                          {title}
                        </VSNTypography>
                        <VSNTypography
                          sx={{
                            fontSize: "14px !important",
                            fontWeight: 400,
                            color: "#0A0A0B",
                            lineHeight: "20px",
                          }}
                        >
                          {description}
                        </VSNTypography>
                      </Box>
                    </Stack>
                  );
                }
              )}
            </Stack>

            <VSNTypography
              component="p"
              className={classes.desc}
              sx={{ fontSize: "14px !important" }}
              mt={2}
              textAlign="right"
            >
              {result?.doctors_take?.signed_by ?? ""}
            </VSNTypography>
          </Stack>

          <PDFRecommendationSection
            data={result?.be_proactive ?? {}}
            staticData={ultraSoundPoliceswiperData}
          />
        </Stack>
      </Box>
    </Box>
  );
});

export default function UltraSoundPoliceResult({
  endPointToCall,
  showBreadCrumbs = true,
  breadCrumbAndButton,
  releaseButton,
  assessmentUserInfo,
  viewAllResult,
  from,
}) {
  const classes = PhysicalOnePagerResultStyle();

  const {
    AssessmentResult: containerResult,
    loader,
    currentAssessment,
  } = from === "viewAll" ? {} : MRFResultContainer({ endPointToCall });

  const AssessmentResult = from === "viewAll" ? viewAllResult : containerResult;

  const [loading, setLoading] = useState(false);
  const printRef = useRef();

  const breadcrumbsRoutes = [
    {
      path: "/assessments",
      label: "Assessments",
    },
    {
      path: `/assessments/${currentAssessment?.assessment_id}`,
      label: `Ultrasound`,
    },
  ];

  const handleDownloadPDF = () => {
    setLoading(true);
    const element = printRef.current;

    const adminFilename = `${assessmentUserInfo?.name}_${assessmentUserInfo?.assessment_name}.pdf`;
    const userFilename = `${AssessmentResult?.info?.name}_${currentAssessment?.assessment_name}.pdf`;
    const fileName = assessmentUserInfo ? adminFilename : userFilename;

    const options = {
      margin: [1, 4],
      filename: fileName,

      image: { type: "jpeg", quality: 0.7 },
      html2canvas: { scale: 1.7, useCORS: true, allowTaint: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    html2pdf()
      .from(element)
      .set(options)
      .toPdf()
      .get("pdf")
      .then((pdf) => {
        pdf.save(fileName);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const PDFDetails = assessmentUserInfo ?? AssessmentResult?.info;

  if (loader?.assessmentResult || !AssessmentResult) {
    return <VSNLoader variant="fullPage" />;
  }

  return (
    <div className={classes.mainLayout}>
      {from !== "viewAll" ? (
        showBreadCrumbs ? (
          <Box mt={2.5} mb={1.8}>
            <VSNBreadcrumbs {...{ breadcrumbsRoutes }} />
          </Box>
        ) : (
          <Box className={classes.headingButton}>
            <Box>{breadCrumbAndButton}</Box>
            <Stack
              direction="row"
              gap={{ sm: 2, xs: 1 }}
              alignItems="center"
              flexWrap="wrap"
              sx={{ width: { xsm: "auto", xs: "100%" } }}
            >
              <VSNButton
                className={classes.PrintButton}
                handleClick={handleDownloadPDF}
                isLoading={loading}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <PrintOutlinedIcon />
                  Print Ultrasound
                </Stack>
              </VSNButton>
              {AssessmentResult?.is_locked ? releaseButton : ""}
            </Stack>
          </Box>
        )
      ) : null}

      {AssessmentResult?.message === "No report found" ? (
        <Stack direction="row" justifyContent="center" p={5}>
          <VSNTypography variant="h5">No Report Found</VSNTypography>
        </Stack>
      ) : (
        <>
          {showBreadCrumbs && (
            <Stack
              direction={{ xsm: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <VSNTypography
                fontSize={{ xsm: 24, sm: 34 }}
                sx={{ fontWeight: 700 }}
              >
                {AssessmentResult?.title ?? ""}
              </VSNTypography>
              <VSNButton
                className={classes.PrintButton}
                handleClick={handleDownloadPDF}
                isLoading={loading}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <PrintOutlinedIcon />
                  Print Ultrasound Report
                </Stack>
              </VSNButton>
            </Stack>
          )}
          <PrintComponent
            ref={printRef}
            result={AssessmentResult}
            header={PDFDetails}
          />

          <Grid container spacing={3}>
            <Grid item md={12} xs={12}>
              <Stack className={classes.assessmentReportCard}>
                <Box sx={{ display: "block" }}>
                  <VSNTypography className={classes.mdHeadingMain}>
                    {AssessmentResult?.clinical_summary?.title ?? ""}
                  </VSNTypography>
                </Box>
                {/* <StarIcon /> */}
                <Stack direction={{ md: "row" }} spacing={2}>
                  <Box flex={1}>
                    <List
                      className={
                        AssessmentResult?.clinical_summary?.content?.length ===
                        1
                          ? classes.notesListOne
                          : classes.notesList
                      }
                    >
                      {AssessmentResult?.clinical_summary?.content?.map(
                        (el, index) => (
                          <React.Fragment key={index}>
                            <ListItem>{el?.text}</ListItem>
                          </React.Fragment>
                        )
                      )}
                    </List>
                  </Box>
                </Stack>
              </Stack>
            </Grid>

            <Grid item xl={6} lg={6} xs={12}>
              <Stack className={classes.assessmentReportCard}>
                <VSNTypography
                  variant="h3"
                  component="h3"
                  className={classes.mdHeadingprimary}
                  mb={1}
                >
                  {AssessmentResult?.doctors_take?.title ?? ""}
                </VSNTypography>
                <VSNTypography component="p" className={classes.desc}>
                  {AssessmentResult?.doctors_take?.description ?? ""}
                </VSNTypography>
                <Box
                  sx={{
                    padding: "20px",
                    display: { sm: "none", xsm: "block" },
                  }}
                  position="relative"
                >
                  <Stack
                    direction="row"
                    spacing={1.4}
                    className={classes.customArrow}
                  >
                    <div className="swiper-button image-swiper-button-next" />
                    <div className="swiper-button image-swiper-button-prev" />
                  </Stack>
                  <VSNSwiper
                    className={classes.containerCard}
                    navigation={{
                      prevEl: ".image-swiper-button-next",
                      nextEl: ".image-swiper-button-prev",
                      disabledClass: "swiper-button-disabled",
                    }}
                    breakpoints={{
                      0: {
                        slidesPerView: 1,
                        spaceBetween: 20,
                      },
                    }}
                  >
                    {AssessmentResult?.doctors_take?.organs?.map(
                      ({ icon, name, description, db_key }, index) => {
                        const matchOrgan = UltrasoundOrgan?.find(
                          (item) => item.key === db_key
                        );

                        return (
                          <SwiperSlide key={index}>
                            <VSNPaper
                              className={classes.containerCardDeatils}
                              sx={{
                                backgroundColor: "#F2E7F5",
                                borderRadius: "12px",
                                border: "1px solid #8B3F9E",
                                textAlign: "start",
                              }}
                            >
                              <Box key={index} className="card">
                                <VSNImage
                                  src={matchOrgan?.svgIcon ?? icon}
                                  alt="icon"
                                />
                              </Box>
                              <VSNTypography
                                variant="subtitle1"
                                component="h6"
                                color="#202024"
                                fontWeight={700}
                                mt={1}
                              >
                                {name}
                              </VSNTypography>
                              <VSNTypography className="paragraph">
                                {description}
                              </VSNTypography>
                            </VSNPaper>
                          </SwiperSlide>
                        );
                      }
                    )}
                  </VSNSwiper>
                </Box>
                <Box
                  sx={{
                    paddingTop: "20px",
                    display: { sm: "block", xs: "none" },
                  }}
                >
                  <CustomSlider data={AssessmentResult?.doctors_take?.organs} />
                </Box>
                <VSNTypography component="p" className={classes.desc} pt={2}>
                  {AssessmentResult?.doctors_take?.signed_by ?? ""}
                </VSNTypography>
              </Stack>
            </Grid>

            <Grid item xl={6} lg={6} xs={12}>
              <Stack className={classes.assessmentReportCardWhite}>
                <VSNTypography
                  variant="h3"
                  component="h3"
                  className={classes.mdHeading}
                  mb={1}
                >
                  {AssessmentResult?.vasana_insights?.title ?? ""}
                </VSNTypography>
                <VSNTypography component="p" className={classes.desc}>
                  {AssessmentResult?.vasana_insights?.description ?? ""}
                </VSNTypography>
                <List
                  className={`${classes.dailyFunctioning} ${classes.dailyFunctioningHeight}`}
                >
                  {AssessmentResult?.vasana_insights?.content?.map(
                    (el, index) => (
                      <ListItem key={index}>
                        <Box mt={0.5}>
                          {/* <PurpalFlowerBullet /> */}
                          <VSNImage src={el?.icon} alt="listicon" />
                        </Box>
                        <VSNTypography color="var(--blackGray)">
                          <span style={{ color: "#86650A", fontWeight: 500 }}>
                            {el?.title ?? ""}{" "}
                          </span>
                          <br />
                          {el?.description}
                        </VSNTypography>
                      </ListItem>
                    )
                  )}
                </List>
              </Stack>
            </Grid>

            <SwiperCardComponent
              title={AssessmentResult?.be_proactive?.title ?? ""}
              description={AssessmentResult?.be_proactive?.description ?? ""}
              data={AssessmentResult?.be_proactive?.recommendations ?? []}
            />
          </Grid>
        </>
      )}
    </div>
  );
}
